import { useState, useEffect } from 'react';
import { aquaApi } from '../aquaApi';
import { fmtDate } from '../dateUtils';
import DateInput from '../DateInput';

const EMPTY = {
  cropId: '', pondNo: '', sampleDate: new Date().toISOString().split('T')[0],
  doc: '', s1: '', s2: '', s3: '', s4: '', s5: '', s6: '',
  avgCount: '', weightGm: '', diff: '', growth: '',
  isPartialHarvest: false, harvestKg: '', notes: ''
};

function calcDocFromRelease(pondNo, sampleDate, releaseMap) {
  const rd = releaseMap[pondNo];
  if (!rd || !sampleDate) return '';
  const diff = Math.floor((new Date(sampleDate) - new Date(rd)) / 86400000);
  return diff > 0 ? String(diff) : '';
}

const fmt2 = (v) => v != null && v !== '' ? parseFloat(v).toFixed(3) : '—';
const fmtCount = (v) => v != null && v !== '' ? parseFloat(v).toFixed(0) : '—';

export default function AquaSampling({ cropId, cropCode, activeCrops = [], role = 'admin', businessTitle = '' }) {
  const canWrite = role !== 'readonly';
  const canDelete = role === 'admin';
  const [rows, setRows]             = useState([]);
  const [progress, setProgress]     = useState([]);
  const [form, setForm]             = useState({ ...EMPTY, cropId: cropId || '' });
  const [editId, setEditId]         = useState(null);
  const [tab, setTab]               = useState('list');
  const [filterPond, setFilterPond] = useState('ALL');
  const [filterCrop, setFilterCrop] = useState(cropId || '');
  const [loading, setLoading]       = useState(true);
  const [err, setErr]               = useState('');
  const [farmPonds, setFarmPonds]   = useState([]);
  const [releaseMap, setReleaseMap] = useState({});

  const load = () => {
    setLoading(true);
    Promise.all([
      aquaApi.getSampling(filterCrop || null),
      filterCrop ? aquaApi.getSamplingProgress(filterCrop) : Promise.resolve([])
    ]).then(([r, p]) => { setRows(r); setProgress(p); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filterCrop]);
  useEffect(() => { setFilterCrop(cropId || ''); }, [cropId]);
  useEffect(() => { setForm(f => ({ ...f, cropId: cropId || '' })); }, [cropId]);

  useEffect(() => {
    aquaApi.getPonds().then(d => { if (Array.isArray(d)) setFarmPonds(d.slice().sort((a, b) => a.pond_name.localeCompare(b.pond_name))); }).catch(() => {});
  }, []);

  useEffect(() => {
    const cid = form.cropId;
    if (!cid) { setReleaseMap({}); return; }
    aquaApi.getCropRelease(cid).then(data => {
      if (Array.isArray(data)) {
        const map = {};
        data.forEach(r => { map[r.pond_no] = r.release_date; });
        setReleaseMap(map);
      }
    }).catch(() => setReleaseMap({}));
  }, [form.cropId]);

  // Recalculate DOC when releaseMap loads or sampleDate changes (handles async timing + date edits)
  useEffect(() => {
    if (tab !== 'form') return;
    setForm(f => {
      const computed = calcDocFromRelease(f.pondNo, f.sampleDate, releaseMap);
      return { ...f, doc: computed || f.doc };
    });
  }, [releaseMap, form.sampleDate]);

  const set = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    if (['s1', 's2', 's3', 's4', 's5', 's6'].includes(k)) {
      const vals = ['s1', 's2', 's3', 's4', 's5', 's6']
        .map(key => parseFloat(key === k ? v : next[key]))
        .filter(n => !isNaN(n) && n > 0);
      if (vals.length > 0) next.avgCount = (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(4);
      if (next.avgCount && parseFloat(next.avgCount) > 0) {
        next.weightGm = (1000 / parseFloat(next.avgCount)).toFixed(4);
      }
    }
    if (k === 'pondNo' || k === 'sampleDate') {
      const pondNo     = k === 'pondNo'     ? v : next.pondNo;
      const sampleDate = k === 'sampleDate' ? v : next.sampleDate;
      const computed = calcDocFromRelease(pondNo, sampleDate, releaseMap);
      next.doc = computed || next.doc;
    }
    if (k === 'cropId') next.doc = '';
    return next;
  });

  const handleSave = async () => {
    if (!form.cropId) { setErr('Crop is required'); return; }
    if (!form.sampleDate || !form.pondNo) { setErr('Date and Pond are required'); return; }
    try {
      if (editId) await aquaApi.updateSampling(editId, form);
      else await aquaApi.createSampling(form);
      setForm({ ...EMPTY, cropId: cropId || '' }); setEditId(null); setErr(''); setTab('list'); load();
    } catch (e) { setErr(e.message); }
  };

  const handleEdit = (r) => {
    setForm({
      cropId: r.crop_id, pondNo: r.pond_no, sampleDate: r.sample_date || '',
      doc: r.doc || '', s1: r.s1 || '', s2: r.s2 || '', s3: r.s3 || '',
      s4: r.s4 || '', s5: r.s5 || '', s6: r.s6 || '',
      avgCount: r.avg_count || '', weightGm: r.weight_gm || '',
      diff: r.diff || '', growth: r.growth || '',
      isPartialHarvest: r.is_partial_harvest || false,
      harvestKg: r.harvest_kg || '', notes: r.notes || ''
    });
    setEditId(r.id); setTab('form');
  };

  const showCropCol = !filterCrop;
  const displayed   = rows.filter(r => (filterPond === 'ALL' || r.pond_no === filterPond));

  // Latest ABW per pond from progress data
  const latestAbw = {};
  progress.forEach(r => { latestAbw[r.pond_no] = r; });

  if (loading) return <div className="loading-msg">Loading sampling data…</div>;

  return (
    <div>
      <div className="tabs">
        <button className={`tab${tab === 'list' ? ' active' : ''}`} onClick={() => { setTab('list'); setEditId(null); }}>📋 Sample Log</button>
        {canWrite && <button className={`tab${tab === 'form' ? ' active' : ''}`} onClick={() => setTab('form')}>{editId ? '✏️ Edit' : '➕ Add Sample'}</button>}
        <button className={`tab${tab === 'growth' ? ' active' : ''}`} onClick={() => setTab('growth')}>📈 Growth Chart</button>
        <button className={`tab${tab === 'summary' ? ' active' : ''}`} onClick={() => setTab('summary')}>📊 Sampling Summary</button>
      </div>

      {/* Crop filter banner */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '8px 12px', background: '#f8f9fa', borderRadius: 8, border: '1px solid #e8eaed' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#5f6368' }}>🦐 Crop:</span>
        <select className="form-control" style={{ width: 200, padding: '4px 8px' }}
          value={filterCrop} onChange={e => { setFilterCrop(e.target.value); setForm(f => ({ ...f, cropId: e.target.value })); }}>
          <option value="">All Active Crops</option>
          {activeCrops.slice().sort((a, b) => a.crop_code.localeCompare(b.crop_code)).map(c => <option key={c.id} value={c.id}>{c.crop_code}</option>)}
        </select>
        {!filterCrop && <span style={{ fontSize: 12, color: '#1a73e8', fontWeight: 600 }}>Showing all crops</span>}
      </div>

      {tab === 'list' && (
        <div>
          {/* Current status cards — only meaningful when a crop is selected */}
          {filterCrop && <div className="stats-grid" style={{ marginBottom: 16 }}>
            {farmPonds.map(p => {
              const latest = progress.filter(r => r.pond_no === p.pond_name).slice(-1)[0];
              return (
                <div className="stat-card" key={p.id}>
                  <div className="stat-icon" style={{ background: '#e8f0fe' }}>
                    <span style={{ color: '#1a73e8', fontWeight: 700, fontSize: 15 }}>{p.pond_name}</span>
                  </div>
                  <div>
                    <div className="stat-label">Latest ABW</div>
                    <div className="stat-value" style={{ fontSize: 20 }}>{latest ? fmt2(latest.weight_gm) + 'g' : '—'}</div>
                    {latest && <div style={{ fontSize: 11, color: '#888' }}>DOC {latest.doc} · Count {fmtCount(latest.avg_count)}</div>}
                  </div>
                </div>
              );
            })}
          </div>}

          <div className="card">
            <div className="card-header">
              <div className="card-title">🔬 Sampling Records {filterCrop && cropCode ? `— ${cropCode}` : ''}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <label style={{ fontSize: 13 }}>Pond:</label>
                <select className="form-control" style={{ width: 100, padding: '4px 8px' }} value={filterPond} onChange={e => setFilterPond(e.target.value)}>
                  <option value="ALL">All</option>
                  {farmPonds.map(p => <option key={p.id} value={p.pond_name}>{p.pond_name}</option>)}
                </select>
              </div>
            </div>
            <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    {showCropCol && <th>Crop</th>}
                    <th>Date</th><th>DOC</th><th>Pond</th>
                    <th style={{ textAlign: 'right' }}>S1</th><th style={{ textAlign: 'right' }}>S2</th>
                    <th style={{ textAlign: 'right' }}>S3</th><th style={{ textAlign: 'right' }}>S4</th>
                    <th style={{ textAlign: 'right' }}>S5</th><th style={{ textAlign: 'right' }}>S6</th>
                    <th style={{ textAlign: 'right' }}>Avg Count</th>
                    <th style={{ textAlign: 'right' }}>ABW (g)</th>
                    <th style={{ textAlign: 'right' }}>Growth (g)</th>
                    <th>Harvest</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayed.length === 0 && <tr><td colSpan={showCropCol ? 15 : 14} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No sampling records</td></tr>}
                  {displayed.map(r => (
                    <tr key={r.id} style={{ background: r.is_partial_harvest ? '#fef3cd' : undefined }}>
                      {showCropCol && <td><span style={{ background: '#e8f0fe', color: '#1a73e8', borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{r.crop_code}</span></td>}
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.sample_date)}</td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{r.doc}</td>
                      <td><span style={{ background: '#e8f0fe', color: '#1a73e8', borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{r.pond_no}</span></td>
                      {['s1','s2','s3','s4','s5','s6'].map(s => (
                        <td key={s} style={{ textAlign: 'right', color: '#666' }}>{fmtCount(r[s])}</td>
                      ))}
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtCount(r.avg_count)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#1a73e8' }}>{fmt2(r.weight_gm)}</td>
                      <td style={{ textAlign: 'right', color: parseFloat(r.growth) > 0 ? '#137333' : '#c5221f' }}>
                        {r.growth ? (parseFloat(r.growth) > 0 ? '+' : '') + fmt2(r.growth) : '—'}
                      </td>
                      <td>{r.is_partial_harvest ? <span style={{ color: '#e37400', fontWeight: 600 }}>⚠️ {r.harvest_kg} Kg</span> : '—'}</td>
                      <td>
                        {canWrite && <button className="btn-sm btn-secondary" onClick={() => handleEdit(r)} style={{ marginRight: 4 }}>Edit</button>}
                        {canDelete && <button className="btn-sm btn-danger" onClick={async () => { if (window.confirm('Delete?')) { await aquaApi.deleteSampling(r.id); load(); } }}>Del</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'form' && (
        <div className="card">
          <div className="card-header"><div className="card-title">{editId ? '✏️ Edit Sample' : '➕ Add Sampling Record'}</div></div>
          <div className="card-body">
            {err && <div className="alert alert-error" style={{ marginBottom: 12 }}>{err}</div>}
            <div className="form-grid cols-3">
              <div className="form-group">
                <label className="form-label">Crop *</label>
                <select className="form-control" value={form.cropId} onChange={e => set('cropId', e.target.value)}>
                  <option value="">— Select Crop —</option>
                  {activeCrops.slice().sort((a, b) => a.crop_code.localeCompare(b.crop_code)).map(c => <option key={c.id} value={c.id}>{c.crop_code}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Sample Date *</label>
                <DateInput className="form-control" value={form.sampleDate} onChange={e => set('sampleDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">DOC
                  <span style={{ fontSize: 10, fontWeight: 400, color: '#1a73e8', marginLeft: 6 }}>auto from release date</span>
                </label>
                <input className="form-control" type="number" min="1" step="1" placeholder="auto" value={form.doc} onChange={e => set('doc', e.target.value)}
                  style={form.doc ? { background: 'rgba(26,115,232,.08)', color: '#1a73e8', fontWeight: 700 } : {}} />
              </div>
              <div className="form-group">
                <label className="form-label">Pond *</label>
                <select className="form-control" value={form.pondNo} onChange={e => set('pondNo', e.target.value)}>
                  <option value="">— Select Pond —</option>
                  {farmPonds.map(p => <option key={p.id} value={p.pond_name}>{p.pond_name}</option>)}
                </select>
              </div>
            </div>

            <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 16, margin: '12px 0' }}>
              <div style={{ fontWeight: 600, marginBottom: 10, color: '#333' }}>Cast Net Counts (pieces per cast)</div>
              <div className="form-grid cols-6">
                {['s1','s2','s3','s4','s5','s6'].map((s, i) => (
                  <div className="form-group" key={s}>
                    <label className="form-label">Cast {i+1}</label>
                    <input className="form-control" type="number" step="0.1" placeholder="—" value={form[s]} onChange={e => set(s, e.target.value)} />
                  </div>
                ))}
              </div>
              {form.avgCount && (
                <div style={{ marginTop: 8, display: 'flex', gap: 24, fontSize: 14 }}>
                  <span style={{ color: '#1a73e8', fontWeight: 600 }}>Avg Count: {parseFloat(form.avgCount).toFixed(1)}</span>
                  <span style={{ color: '#137333', fontWeight: 600 }}>ABW: {form.weightGm ? parseFloat(form.weightGm).toFixed(3) + ' g' : '—'}</span>
                </div>
              )}
            </div>

            <div className="form-grid cols-3">
              <div className="form-group">
                <label className="form-label">Avg Count (override)</label>
                <input className="form-control" type="number" step="0.01" value={form.avgCount} onChange={e => set('avgCount', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">ABW / Weight (g/pc)</label>
                <input className="form-control" type="number" step="0.0001" value={form.weightGm} onChange={e => set('weightGm', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Growth since last (g)</label>
                <input className="form-control" type="number" step="0.0001" value={form.growth} onChange={e => set('growth', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">
                  <input type="checkbox" checked={form.isPartialHarvest} onChange={e => set('isPartialHarvest', e.target.checked)} style={{ marginRight: 6 }} />
                  Partial Harvest
                </label>
                <input className="form-control" type="number" step="0.01" placeholder="Harvest Kg" value={form.harvestKg} onChange={e => set('harvestKg', e.target.value)} disabled={!form.isPartialHarvest} />
              </div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label className="form-label">Notes</label>
                <input className="form-control" value={form.notes} onChange={e => set('notes', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={handleSave}>{editId ? 'Update' : 'Save'}</button>
              <button className="btn btn-secondary" onClick={() => { setTab('list'); setEditId(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {tab === 'summary' && (() => {
        // ── Build sessions: group rows by sample_date, sorted ascending ──
        const sorted = [...rows].sort((a, b) =>
          (a.sample_date || '').localeCompare(b.sample_date || '') ||
          (a.pond_no || '').localeCompare(b.pond_no || '')
        );
        const sessionMap = new Map();
        sorted.forEach(r => {
          const d = r.sample_date || '';
          if (!sessionMap.has(d)) sessionMap.set(d, []);
          sessionMap.get(d).push(r);
        });
        const sessions = Array.from(sessionMap.entries()); // [[date, rows[]], ...]

        // Collect all unique ponds across all sessions (sorted)
        const allPonds = [...new Set(sorted.map(r => r.pond_no))].sort();

        // Format date as DD.MM.YY
        const fmtD = (d) => {
          if (!d) return '';
          const [y, m, dd] = (d || '').split('-');
          return `${dd}.${m}.${y ? y.slice(2) : ''}`;
        };
        const fmtAbw  = (v) => v != null && v !== '' && parseFloat(v) > 0 ? parseFloat(v).toFixed(1) : '-';
        const fmtCnt  = (v) => v != null && v !== '' && parseFloat(v) > 0 ? Math.round(parseFloat(v)) : '-';
        const fmtGrwth = (v) => v != null && v !== '' && parseFloat(v) !== 0 ? parseFloat(v).toFixed(1) : '-';
        const fmtDiff  = (v) => { const n = parseFloat(v); return isNaN(n) || n === 0 ? '0' : Math.round(n).toString(); };

        // Cell styles
        const B = '1px solid #b0b8c8';
        const hdrCell = (extra = {}) => ({
          border: B, padding: '5px 8px', textAlign: 'center',
          fontWeight: 700, color: '#fff', fontSize: 12, ...extra,
        });
        const dataCell = (extra = {}) => ({
          border: B, padding: '6px 8px', textAlign: 'center',
          fontSize: 13, verticalAlign: 'middle', ...extra,
        });

        return (
          <div style={{ overflowX: 'auto', fontFamily: 'Arial, sans-serif' }}>
            {/* Firm + crop header */}
            <div style={{
              background: 'linear-gradient(135deg,#1a237e,#283593)',
              color: '#fff', padding: '14px 20px',
              fontSize: 22, fontWeight: 900, letterSpacing: 1,
              marginBottom: 8, borderRadius: 6,
            }}>
              {[businessTitle, cropCode].filter(Boolean).join(' / ')}
            </div>

            {sessions.length === 0 && (
              <div style={{ textAlign: 'center', color: '#888', padding: 48, fontSize: 15 }}>
                No sampling data{!filterCrop ? ' — select a crop above' : ''}
              </div>
            )}

            {sessions.map(([date, sRows], si) => {
              // DOC for this session — use max doc in session
              const docVal = sRows.reduce((mx, r) => Math.max(mx, parseInt(r.doc || 0, 10)), 0) || '';

              // Previous session DOC
              const prevDoc = si > 0
                ? sessions[si - 1][1].reduce((mx, r) => Math.max(mx, parseInt(r.doc || 0, 10)), 0)
                : 0;
              const daysSince = si === 0 ? (docVal || '') : (docVal ? docVal - prevDoc : '');

              // Build pond → row lookup for this session
              const pondRow = {};
              sRows.forEach(r => { pondRow[r.pond_no] = r; });

              // Avg growth across ponds that have data in this session
              const growthVals = sRows
                .map(r => parseFloat(r.growth))
                .filter(n => !isNaN(n) && n > 0);
              const avgGrowth = growthVals.length > 0
                ? (growthVals.reduce((a, b) => a + b, 0) / growthVals.length).toFixed(1)
                : null;

              return (
                <table key={date} style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20, minWidth: 620 }}>
                  <tbody>
                    {/* ── Date / DOC header row ── */}
                    <tr>
                      <td colSpan={3} style={{
                        background: '#4a6fa5', color: '#fff',
                        padding: '10px 16px', fontSize: 18, fontWeight: 800,
                        border: B, letterSpacing: 0.5,
                      }}>
                        {fmtD(date)}
                      </td>
                      <td colSpan={4} style={{
                        background: '#5b7fa6', color: '#fff',
                        padding: '10px 12px', fontSize: 13, fontWeight: 700,
                        border: B, textAlign: 'center',
                      }}>
                        {/* time not stored — placeholder */}
                      </td>
                      <td colSpan={2} style={{
                        background: '#4a6fa5', color: '#fff',
                        padding: '10px 12px', fontSize: 32, fontWeight: 900,
                        border: B, textAlign: 'center', lineHeight: 1,
                      }}>
                        {docVal}
                      </td>
                      <td colSpan={3} style={{
                        background: '#4a6fa5', color: '#fff',
                        padding: '10px 16px', fontSize: 26, fontWeight: 900,
                        border: B, textAlign: 'right', letterSpacing: 2,
                      }}>
                        DOC
                      </td>
                    </tr>

                    {/* ── Column sub-headers ── */}
                    <tr>
                      <th style={{ ...hdrCell(), background: '#546e7a', width: 44 }}></th>
                      <th style={{ ...hdrCell(), background: '#546e7a' }}>S1</th>
                      <th style={{ ...hdrCell(), background: '#546e7a' }}>S2</th>
                      <th style={{ ...hdrCell(), background: '#546e7a' }}>S3</th>
                      <th style={{ ...hdrCell(), background: '#546e7a' }}>S4</th>
                      <th style={{ ...hdrCell(), background: '#546e7a' }}>S5</th>
                      <th style={{ ...hdrCell(), background: '#546e7a' }}>S6</th>
                      <th style={{ ...hdrCell(), background: '#455a64', fontSize: 11 }}>AVG CNT</th>
                      <th style={{ ...hdrCell(), background: '#455a64', fontSize: 11 }}>Grms/Pc</th>
                      <th style={{ ...hdrCell(), background: '#455a64' }}>DIFF</th>
                      <th style={{ ...hdrCell(), background: '#37474f', fontSize: 11 }}>GRWTH</th>
                    </tr>

                    {/* ── Pond data rows ── */}
                    {allPonds.map((pond, pi) => {
                      const r = pondRow[pond];
                      const avgCnt = r ? fmtCnt(r.avg_count) : '';
                      const abw    = r ? fmtAbw(r.weight_gm) : '';
                      const diff   = r ? fmtDiff(r.diff)    : '0';
                      const grwth  = r ? fmtGrwth(r.growth) : '';
                      const diffN  = r ? parseInt(r.diff, 10) : 0;

                      return (
                        <tr key={pond} style={{ background: pi % 2 === 0 ? '#f0f4ff' : '#fff' }}>
                          {/* Pond label */}
                          <td style={{
                            ...dataCell(),
                            background: '#5c6bc0', color: '#fff',
                            fontWeight: 900, fontSize: 18,
                          }}>
                            {pond}
                          </td>
                          {/* S1–S6 */}
                          {['s1','s2','s3','s4','s5','s6'].map(s => (
                            <td key={s} style={{ ...dataCell(), color: '#444' }}>
                              {r && r[s] != null && r[s] !== '' && parseFloat(r[s]) > 0
                                ? Math.round(parseFloat(r[s]))
                                : '-'}
                            </td>
                          ))}
                          {/* AVG CNT — large red */}
                          <td style={{ ...dataCell(), color: '#c5221f', fontWeight: 900, fontSize: 22, lineHeight: 1 }}>
                            {avgCnt}
                          </td>
                          {/* Grms/Pc (ABW) */}
                          <td style={{ ...dataCell(), fontWeight: 700, fontSize: 15, color: '#1a237e' }}>
                            {abw}
                          </td>
                          {/* DIFF */}
                          <td style={{ ...dataCell(), color: diffN < 0 ? '#c5221f' : diffN > 0 ? '#137333' : '#888', fontWeight: 700 }}>
                            {r ? diff : '0'}
                          </td>
                          {/* GRWTH — large bold */}
                          <td style={{ ...dataCell(), color: '#c5221f', fontWeight: 900, fontSize: 20, lineHeight: 1 }}>
                            {grwth}
                          </td>
                        </tr>
                      );
                    })}

                    {/* ── AVG GROWTH footer row ── */}
                    <tr style={{ background: '#b8b860' }}>
                      <td colSpan={4} style={{ ...dataCell(), background: '#b8b860', color: '#1a237e', fontWeight: 800, fontSize: 14, textAlign: 'center', letterSpacing: 0.5 }}>
                        AVG GROWTH in
                      </td>
                      <td colSpan={3} style={{ ...dataCell(), background: '#b8b860', color: '#1a237e', fontWeight: 900, fontSize: 28, lineHeight: 1, textAlign: 'center' }}>
                        {daysSince}
                      </td>
                      <td style={{ ...dataCell(), background: '#b8b860', color: '#555', fontWeight: 700, fontSize: 11 }}>DAYs</td>
                      <td style={{ ...dataCell(), background: '#b8b860', color: '#1a237e', fontWeight: 900, fontSize: 20 }}>
                        {avgGrowth ?? '-'}
                      </td>
                      <td colSpan={2} style={{ ...dataCell(), background: '#b8b860', color: '#555', fontWeight: 700, fontSize: 13 }}>Grms</td>
                    </tr>
                  </tbody>
                </table>
              );
            })}
          </div>
        );
      })()}

      {tab === 'growth' && (
        <div>
          {farmPonds.map(p => {
            const pData = progress.filter(r => r.pond_no === p.pond_name);
            if (pData.length === 0) return null;
            const maxAbw = Math.max(...pData.map(r => parseFloat(r.weight_gm || 0)));
            return (
              <div className="card" key={p.id} style={{ marginBottom: 16 }}>
                <div className="card-header"><div className="card-title">📈 {p.pond_name} — Growth Progression</div></div>
                <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr><th>Date</th><th>DOC</th><th style={{ textAlign: 'right' }}>Avg Count</th><th style={{ textAlign: 'right' }}>ABW (g)</th><th style={{ textAlign: 'right' }}>Growth (g)</th><th>Visual</th><th>Harvest</th></tr>
                    </thead>
                    <tbody>
                      {pData.map(r => {
                        const pct = maxAbw > 0 ? (parseFloat(r.weight_gm || 0) / maxAbw) * 100 : 0;
                        return (
                          <tr key={r.id}>
                            <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.sample_date)}</td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>{r.doc}</td>
                            <td style={{ textAlign: 'right' }}>{fmtCount(r.avg_count)}</td>
                            <td style={{ textAlign: 'right', fontWeight: 700, color: '#1a73e8' }}>{fmt2(r.weight_gm)} g</td>
                            <td style={{ textAlign: 'right', color: parseFloat(r.growth) > 0 ? '#137333' : '#5f6368' }}>
                              {r.growth ? (parseFloat(r.growth) > 0 ? '+' : '') + fmt2(r.growth) + ' g' : '—'}
                            </td>
                            <td style={{ width: 140 }}>
                              <div style={{ height: 12, background: '#e8eaed', borderRadius: 6, overflow: 'hidden' }}>
                                <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg,#1a73e8,#34a853)', borderRadius: 6, transition: 'width .3s' }} />
                              </div>
                            </td>
                            <td>{r.is_partial_harvest ? <span style={{ color: '#e37400', fontSize: 12 }}>⚠️ {r.harvest_kg} Kg</span> : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
          {progress.length === 0 && <div className="card"><div className="card-body" style={{ textAlign: 'center', color: '#888', padding: 40 }}>No sampling data yet</div></div>}
        </div>
      )}
    </div>
  );
}
